
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/hooks/auth";

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export const useLoadUserData = () => {
  const { user } = useAuth();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);
      try {
        // First check for user from auth context
        if (user) {
          console.log("User found in auth context:", user);
          
          // Basic user data from auth context
          const basicUserData = {
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
            phone: user.phone || ""
          };
          
          console.log("Setting user data from auth context:", basicUserData);
          setUserData(basicUserData);
        } else {
          // Fall back to localStorage if no user in auth context
          const userId = localStorage.getItem('userId');
          if (!userId) {
            setIsLoading(false);
            return;
          }
          
          try {
            // First try to get data from profiles table
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('first_name, last_name, phone')
              .eq('id', userId)
              .single();
              
            if (profileError && profileError.code !== 'PGRST116') {
              console.error('Error fetching user profile:', profileError);
            }
            
            // Then try to get additional data from users table (email)
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('email')
              .eq('id', userId)
              .single();
              
            if (userError) {
              console.error('Error fetching user email:', userError);
              setIsLoading(false);
              return;
            }
            
            if (profileData || userData) {
              const formattedUserData = {
                firstName: profileData?.first_name || "",
                lastName: profileData?.last_name || "",
                email: userData?.email || "",
                phone: profileData?.phone || "",
              };
              
              console.log("Setting user data from database:", formattedUserData);
              setUserData(formattedUserData);
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [user]);

  return {
    userData,
    isLoading
  };
};
